import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import Loader from 'react-loader-spinner';
import { FiArrowUp, FiArrowDown, FiX, FiPlus } from 'react-icons/fi';
import SaveBar from './SaveBar';
import PartnerModal from './PartnerModal';
import {
    fetchAdminPartners,
    setLeaguePartners,
    savePartner,
    deletePartner,
    partnerErrorText,
} from '../actions/partnersActions';
import { BASE_URL } from '../constants/config';
import {
    leaguePartnersFrom,
    leaguePartnersPayload,
    sameLeaguePartners,
    moveItem,
} from '../utilities/partners';
import '../styles/partners.css';

// Вкладка «Спонсоры» в настройках лиги: какие спонсоры у лиги, в каком порядке
// и кого показывать. Привязка общая для всех сезонов (related_partners).
// props: league, groups (чемпионаты с лигами — для создания нового спонсора),
// onToast(text, type)

const resolveImage = (image) => {
    if (!image) return null;
    return image.startsWith('http') || image.startsWith('data:') ? image : BASE_URL.url + image;
};

export default function LeaguePartnersPanel({ league, groups, onToast }) {
    const dispatch = useDispatch();
    const [partners, setPartners] = useState(null);
    const [error, setError] = useState(null);
    const [draft, setDraft] = useState(null);
    const [saving, setSaving] = useState(false);
    const [modal, setModal] = useState({ open: false, partner: null });

    const load = useCallback(async () => {
        setError(null);
        try {
            setPartners(await dispatch(fetchAdminPartners()));
        } catch (e) {
            console.error('Ошибка загрузки спонсоров:', e);
            setError(partnerErrorText(e, 'Не удалось загрузить спонсоров'));
        }
    }, [dispatch]);

    useEffect(() => { load(); }, [load]);

    const byId = useMemo(() => {
        const map = {};
        (partners || []).forEach(partner => { map[partner.id] = partner; });
        return map;
    }, [partners]);

    const serverList = useMemo(
        () => (partners ? leaguePartnersFrom(partners, league.id) : []),
        [partners, league.id]
    );
    const list = draft || serverList;
    const dirty = draft !== null && !sameLeaguePartners(draft, serverList);
    const available = (partners || []).filter(partner => !list.some(item => item.partner_id === partner.id));

    const edit = (updater) => setDraft(updater(list));

    const save = async () => {
        setSaving(true);
        try {
            await dispatch(setLeaguePartners(leaguePartnersPayload(league.id, list)));
            await load();
            setDraft(null);
            onToast('Спонсоры лиги сохранены', 'success');
        } catch (e) {
            console.error('Ошибка сохранения спонсоров лиги:', e);
            onToast(partnerErrorText(e, 'Не удалось сохранить спонсоров'), 'error');
        } finally {
            setSaving(false);
        }
    };

    // Новый спонсор, созданный отсюда, сразу привязан к этой лиге.
    // Если на вкладке есть несохранённый список, сверяем его с новыми лигами спонсора
    const handleSave = async (result) => {
        setSaving(true);
        const isNew = !modal.partner;
        try {
            const id = await dispatch(savePartner(modal.partner, result));
            await load();
            if (draft !== null && result.leaguesChanged) {
                const linked = result.leagueIds.includes(league.id);
                setDraft(prev => {
                    const current = prev || [];
                    const has = current.some(item => item.partner_id === id);
                    if (linked && !has) return [...current, { partner_id: id, active: true }];
                    if (!linked && has) return current.filter(item => item.partner_id !== id);
                    return current;
                });
            }
            onToast(isNew ? 'Спонсор добавлен' : 'Спонсор сохранён', 'success');
            return true;
        } catch (e) {
            console.error('Ошибка сохранения спонсора:', e);
            onToast(partnerErrorText(e, 'Не удалось сохранить спонсора'), 'error');
            load().catch(() => {});
            return false;
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (partner) => {
        setSaving(true);
        try {
            await dispatch(deletePartner(partner.id));
            await load();
            if (draft !== null) setDraft(prev => prev.filter(item => item.partner_id !== partner.id));
            onToast(`Спонсор «${partner.name}» удалён`, 'success');
            return true;
        } catch (e) {
            onToast(partnerErrorText(e, 'Не удалось удалить спонсора'), 'error');
            return false;
        } finally {
            setSaving(false);
        }
    };

    const renderRow = (item, index) => {
        const partner = byId[item.partner_id];
        if (!partner) return null;
        const image = resolveImage(partner.image);
        return (
            <div key={item.partner_id} className={`ptLinkRow ${item.active ? '' : 'ptLinkRowOff'}`}>
                <span className="ptLinkIndex">{index + 1}</span>
                <span className="ptRowLogo ptLinkLogo">
                    {image ? <img src={image} alt="" loading="lazy" /> : partner.name.charAt(0).toUpperCase()}
                </span>
                <button
                    type="button"
                    className="ptLinkMain"
                    onClick={() => setModal({ open: true, partner })}
                    title="Изменить спонсора"
                >
                    <span className="ptRowTitle">{partner.name}</span>
                    {partner.description && <span className="ptMuted">{partner.description}</span>}
                </button>
                <label className="switch" title={item.active ? 'Показывается' : 'Скрыт'}>
                    <input
                        type="checkbox"
                        checked={item.active}
                        disabled={saving}
                        onChange={(e) => edit(prev => prev.map((one, i) => (
                            i === index ? { ...one, active: e.target.checked } : one
                        )))}
                    />
                    <span className="switchSlider" />
                </label>
                <span className="ptLinkMoves">
                    <button
                        type="button"
                        className="ptIconButton"
                        aria-label="Выше"
                        disabled={saving || index === 0}
                        onClick={() => edit(prev => moveItem(prev, index, -1))}
                    >
                        <FiArrowUp />
                    </button>
                    <button
                        type="button"
                        className="ptIconButton"
                        aria-label="Ниже"
                        disabled={saving || index === list.length - 1}
                        onClick={() => edit(prev => moveItem(prev, index, 1))}
                    >
                        <FiArrowDown />
                    </button>
                </span>
                <button
                    type="button"
                    className="ptIconButton ptIconDanger"
                    aria-label="Убрать из лиги"
                    title="Убрать из лиги"
                    disabled={saving}
                    onClick={() => edit(prev => prev.filter((one, i) => i !== index))}
                >
                    <FiX />
                </button>
            </div>
        );
    };

    return (
        <div className="settingsCard">
            <div className="settingsCardHeader">
                <h2>Спонсоры лиги</h2>
                <p>
                    Логотипы на странице лиги в приложении и на сайте, сверху вниз — в этом порядке.
                    Выключенный спонсор остаётся у лиги, но не показывается. Общие для всех сезонов.
                </p>
            </div>
            <div className="settingsCardBody">
                {partners === null && !error && (
                    <div className="teamsLoader">
                        <Loader type="Oval" color="#04a9f5" height={36} width={36} />
                    </div>
                )}
                {error && (
                    <p className="lgError">
                        {error}{' '}
                        <button type="button" className="linkButton" onClick={load}>Повторить</button>
                    </p>
                )}
                {partners !== null && (
                    <>
                        {list.length > 0
                            ? <div className="ptLinkList">{list.map(renderRow)}</div>
                            : <p className="ptEmpty">У лиги пока нет спонсоров.</p>}

                        <div className="ptAddRow">
                            <select
                                className="modalInput"
                                value=""
                                disabled={saving || available.length === 0}
                                onChange={(e) => {
                                    const id = Number(e.target.value);
                                    if (id) edit(prev => [...prev, { partner_id: id, active: true }]);
                                }}
                            >
                                <option value="">
                                    {available.length ? 'Добавить спонсора из списка…' : 'Все спонсоры уже добавлены'}
                                </option>
                                {available.map(partner => (
                                    <option key={partner.id} value={partner.id}>{partner.name}</option>
                                ))}
                            </select>
                            <button
                                type="button"
                                className="lgGhostBtn"
                                disabled={saving}
                                onClick={() => setModal({ open: true, partner: null })}
                            >
                                <FiPlus />
                                Новый спонсор
                            </button>
                        </div>

                        <SaveBar
                            dirty={dirty}
                            onSave={save}
                            onReset={() => setDraft(null)}
                            label="Сохранить спонсоров"
                            title="Спонсоры этой лиги"
                            hint="Спонсоры изменены и не сохранены"
                            saveDisabled={saving}
                            variant="inline"
                        />
                    </>
                )}
            </div>

            <PartnerModal
                isOpen={modal.open}
                partner={modal.partner}
                groups={groups}
                initialLeagueIds={[league.id]}
                saving={saving}
                onClose={() => setModal({ open: false, partner: null })}
                onSave={handleSave}
                onDelete={handleDelete}
            />
        </div>
    );
}
